import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';

import React, { useCallback, useMemo } from 'react';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';

import { getSelectedProject, updateProject } from '../../redux/account';
import { getIsLoading } from '../../redux/headcountChanges';

import _ from 'lodash';
import {
  formatStringToDate,
  dateToIsoString,
  parseFormikErrors,
} from '../../utils';
import { PageWrapper } from '../../components';

import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { DateTime } from 'luxon';
import { CustomIcon, IconName } from '../../components/customIcon/CustomIcon';

export interface ProjectedRevenueEndDateFormik {
  projectedRevenueEndDate: string | undefined;
}

interface Props {
  isModalOpen: boolean;
  handleCloseModal: () => void;
}

const EditProjectedRevenueEndDateModal: React.FC<Props> = ({
  isModalOpen,
  handleCloseModal,
}) => {
  const dispatch = useAppDispatch();
  const isLoading = useAppSelector(getIsLoading);
  const project = useAppSelector(getSelectedProject);

  const initialValues: ProjectedRevenueEndDateFormik = useMemo(() => {
    return {
      projectedRevenueEndDate: project?.projectedRevenueEndDate,
    };
  }, [project?.projectedRevenueEndDate]);

  const formik = useFormik<ProjectedRevenueEndDateFormik>({
    enableReinitialize: true,
    initialValues,
    validationSchema: yup.object().shape({
      projectedRevenueEndDate: yup.date().required('Value is required'),
    }),
    onSubmit: (values) => {
      if (!project || !values.projectedRevenueEndDate) return;

      dispatch(
        updateProject({
          projectId: project.id,
          projectedRevenueEndDate: DateTime.fromISO(
            values.projectedRevenueEndDate,
          ).toFormat('yyyy-LL-dd'),
        }),
      );
      resetForm();
    },
    validateOnMount: false,
    validateOnChange: false,
  });

  const changeProjectedRevenueEndDate = useCallback(
    (date: Date | null) => {
      formik.setFieldValue('projectedRevenueEndDate', dateToIsoString(date));
    },
    [formik],
  );

  const resetForm = useCallback(() => {
    formik.resetForm();
    handleCloseModal();
  }, [formik, handleCloseModal]);

  if (!project) return null;

  return (
    <Dialog
      fullWidth
      onClose={handleCloseModal}
      open={isModalOpen}
      scroll="body"
      maxWidth="sm"
    >
      <DialogTitle>Forecasted account end date</DialogTitle>
      <CloseOutlinedIcon
        aria-label="close"
        sx={{
          position: 'absolute',
          right: 22,
          top: 21,
          color: 'grey',
          cursor: 'pointer',
        }}
        onClick={resetForm}
        fontSize="small"
      ></CloseOutlinedIcon>

      <DialogContent sx={{ maxHeight: 600 }}>
        <PageWrapper isLoading={isLoading} sx={{ paddingBlock: 0 }}>
          <Box
            sx={{ mb: 3, bgcolor: 'highlight.neutral', p: 2, borderRadius: 1 }}
          >
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
              <CustomIcon
                icon={IconName.Info}
                sx={{ mt: 0.5, color: 'text.secondary' }}
              />
              <Box>
                <Typography variant="body1" color="text.secondary">
                  End date for the revenue forecasting.
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Manual or the furthest from SOW sheet.
                </Typography>
              </Box>
            </Box>
          </Box>

          <DatePicker
            onChange={changeProjectedRevenueEndDate}
            value={
              formik.values.projectedRevenueEndDate
                ? formatStringToDate(formik.values.projectedRevenueEndDate)
                : null
            }
            format="MM/dd/yyyy"
            label="End Date"
            disablePast
            disableHighlightToday
            shouldDisableDate={(date) =>
              date.getDate() === new Date().getDate()
            }
            slotProps={{
              textField: {
                error: Boolean(formik.errors.projectedRevenueEndDate),
                id: 'projectedRevenueEndDate',
                label: 'End Date',
                name: 'projectedRevenueEndDate',
              },
            }}
          />

          <Box>
            {formik.errors.projectedRevenueEndDate && (
              <>
                {parseFormikErrors(formik.errors.projectedRevenueEndDate).map(
                  (error) => (
                    <Typography key={error} color="error">
                      {error}
                    </Typography>
                  ),
                )}
              </>
            )}
          </Box>
        </PageWrapper>
      </DialogContent>

      <DialogActions
        sx={{ alignItems: 'stretch', justifyContent: 'space-between' }}
      >
        <Box
          display="flex"
          flexDirection="row"
          alignItems="stretch"
          justifyContent="space-between"
          gap={1}
        >
          <Button onClick={resetForm}>Cancel</Button>
          <Button
            variant="contained"
            onClick={formik.submitForm}
            disabled={!formik.dirty}
          >
            Save
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default EditProjectedRevenueEndDateModal;
